import { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { apiClient } from '@/lib/api-client';
import { DataSource } from '@/types/data-source';
import { toast } from 'sonner';

interface ExtractionPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  dataSource: DataSource | null;
}

interface ExtractionPreviewData {
  data: unknown[];
  totalRows: number;
  jobId: string;
}

export function ExtractionPreviewDialog({
  open,
  onClose,
  dataSource,
}: ExtractionPreviewDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<ExtractionPreviewData | null>(null);

  const fetchPreview = async () => {
    if (!dataSource) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.get<ExtractionPreviewData>(
        `/preview/data-sources/${dataSource.id}`
      );
      setPreviewData(response);
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load preview';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      onClose();
    }
  };

  // Auto-fetch when dialog opens
  useEffect(() => {
    if (open && dataSource) {
      fetchPreview();
    }
  }, [open, dataSource?.id]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Extracted Data Preview</DialogTitle>
          <DialogDescription>
            Viewing extracted data for {dataSource?.name}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto px-6 py-4">
          {isLoading && (
            <div className="flex items-center justify-center h-48">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading preview...</span>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center h-48 gap-2">
              <p className="text-destructive font-medium">Failed to load preview</p>
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button onClick={fetchPreview} variant="outline" size="sm">
                Retry
              </Button>
            </div>
          )}

          {!isLoading && !error && previewData && (
            <div className="space-y-4">
              {/* Stats */}
              <div className="flex items-center gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Job ID:</span>{' '}
                  <span className="font-medium font-mono text-xs">{previewData.jobId}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Total Rows:</span>{' '}
                  <span className="font-medium">{previewData.totalRows}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Showing:</span>{' '}
                  <span className="font-medium">{previewData.data.length} rows</span>
                </div>
              </div>

              {/* Table */}
              {previewData.data.length > 0 ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {Object.keys(previewData.data[0] as Record<string, unknown>).map((key) => (
                          <TableHead key={key}>{key}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.data.slice(0, 50).map((row: any, idx) => (
                        <TableRow key={idx}>
                          {Object.values(row).map((value, cellIdx) => (
                            <TableCell key={cellIdx}>
                              {formatCellValue(value)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  No data available. Run an extraction first.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={fetchPreview} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper to format cell values
function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}
